import { lazy, ReactNode, Suspense } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import ProtectedRoute from '@/routes/ProtectedRoute';
import ApexOnlyRoute from '@/routes/ApexOnlyRoute';

import Home from '@/pages/home';
import Dashboard from '@/pages/dashboard';
import Wealth from '@/pages/wealth';
import Planning from '@/pages/planning';
import FileHub from '@/pages/filehub';
import Tax from '@/pages/tax';
import Insurance from '@/pages/insurance';
import Step1 from '@/pages/wealth/components/open-brokerage-account/step1';
import Step2 from '@/pages/wealth/components/open-brokerage-account/step2';
import BankLinkPicker from '@/pages/wealth/components/bank-link-picker';
import WealthProfile from '@/pages/wealth-profile';
import Agreements from '@/pages/agreements';
import KycPlaidPage from '@/pages/kyc/plaid-kyc';
import KycFailedPage from '@/pages/kyc/failed';

const Login = lazy(() => import('../pages/login'));
const InvestorSignUp = lazy(() => import('../pages/signup/investor'));

const lazyLoad = (children: ReactNode): ReactNode => {
  return <Suspense fallback={<>loading</>}>{children}</Suspense>;
};

const apexOnly = (children: ReactNode): ReactNode => <ApexOnlyRoute>{children}</ApexOnlyRoute>;

const protectedApexOnly = (children: ReactNode): ReactNode => (
  <ProtectedRoute>
    <ApexOnlyRoute>{children}</ApexOnlyRoute>
  </ProtectedRoute>
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: lazyLoad(<Login />),
  },
  {
    path: '/firm/:firmId',
    element: lazyLoad(<Login />),
  },
  {
    path: '/firm/:firmId/signup/investor',
    element: lazyLoad(<InvestorSignUp />),
  },
  {
    path: '/home',
    element: (
      <ProtectedRoute>
        <Home />
      </ProtectedRoute>
    ),
    children: [
      { path: '', element: <Navigate to="wealth" /> },
      { path: 'dashboard', element: lazyLoad(<Dashboard />) },
      { path: 'wealth', element: lazyLoad(<Wealth />) },
      { path: 'wealth/accounts/:accountId', element: lazyLoad(<Wealth />) },
      // Transfers happen in the Wealth modal now; old bookmarks land on Wealth.
      { path: 'deposit', element: <Navigate to="/home/wealth" replace /> },
      { path: 'withdraw', element: <Navigate to="/home/wealth" replace /> },
      { path: 'transfer', element: <Navigate to="/home/wealth" replace /> },
      { path: 'bank-link', element: lazyLoad(apexOnly(<BankLinkPicker />)) },
      { path: 'planning', element: lazyLoad(<Planning />) },
      { path: 'filehub', element: lazyLoad(<FileHub />) },
      { path: 'tax', element: lazyLoad(<Tax />) },
      { path: 'insurance', element: lazyLoad(<Insurance />) },
      { path: 'messages', element: lazyLoad(<FileHub />) },
      { path: 'wealth-profile', element: <WealthProfile /> },
      { path: 'agreements', element: lazyLoad(<Agreements />) },
    ],
  },
  { path: '/kyc', element: protectedApexOnly(<KycPlaidPage />) },
  {
    path: '/kyc/success',
    element: protectedApexOnly(<Navigate to="/wealth/open-brokerage-account/step1" replace />),
  },
  { path: '/kyc/failed', element: protectedApexOnly(<KycFailedPage />) },
  {
    path: '/wealth/open-brokerage-account/step1',
    element: protectedApexOnly(<Step1 />),
  },
  {
    path: '/wealth/open-brokerage-account/step2',
    element: protectedApexOnly(<Step2 />),
  },
];

export default routes;
